import type {
  ContributorPlaylistDto,
  FlowContributorPlaylistsDto,
  SessionContributorPlaylistsDto,
} from '@/types/dtos/contributors/contributor-playlist.dto';
import type { ContributorDto } from '@/types/dtos/contributors/response/contributor.dto';
import type { FlowDto } from '@/types/dtos/flows/response/flow.dto';
import type { SessionDto } from '@/types/dtos/sessions/session.dto';
import { SESSION_ACTOR_ROLES } from '@/types/dtos/contributors/response/contributor.dto';
import { getContributorSessionRoleGeneric, getFlowModerators } from '@/utils/contributor-extraction.utils';

export type PlaylistContributorRow = {
  roleId: string;
  sequence: number;
  contributor: ContributorDto;
};

export function getSessionCrewPlaylist(session: SessionDto): ContributorPlaylistDto | undefined {
  return session.contributorPlaylists?.session;
}

export function getFlowModeratorsPlaylist(flow: FlowDto): ContributorPlaylistDto | undefined {
  return flow.contributorPlaylists?.flow_moderators;
}

export function canReorderContributorPlaylist(playlist?: ContributorPlaylistDto): boolean {
  if (!playlist?.id) {
    return false;
  }
  return Boolean(playlist.permissions?.includes('reorder') || playlist.permissions?.includes('manage'));
}

export function orderContributorsByPlaylist<T extends ContributorDto>(
  contributors: T[],
  playlist: ContributorPlaylistDto | undefined,
  resolveRoleId: (contributor: T) => string | undefined,
): T[] {
  if (!playlist?.refs?.length) {
    return contributors;
  }

  const byRoleId = new Map<string, T>();
  for (const contributor of contributors) {
    const roleId = resolveRoleId(contributor);
    if (roleId) {
      byRoleId.set(roleId, contributor);
    }
  }

  const ordered: T[] = [];
  const seen = new Set<string>();
  for (const ref of [...playlist.refs].sort((a, b) => a.sequence - b.sequence)) {
    const contributor = byRoleId.get(ref.roleId);
    if (contributor && !seen.has(contributor.id)) {
      ordered.push(contributor);
      seen.add(contributor.id);
    }
  }

  for (const contributor of contributors) {
    if (!seen.has(contributor.id)) {
      ordered.push(contributor);
    }
  }

  return ordered;
}

export function buildPlaylistContributorRows(
  playlist: ContributorPlaylistDto | undefined,
  contributors: ContributorDto[],
  resolveRoleId: (contributor: ContributorDto) => string | undefined,
): PlaylistContributorRow[] {
  if (!playlist?.id) {
    return [];
  }

  if (!playlist.refs?.length) {
    return contributors.flatMap((contributor, index) => {
      const roleId = resolveRoleId(contributor);
      if (!roleId) {
        return [];
      }
      return [{ roleId, sequence: index + 1, contributor }];
    });
  }

  const rows: PlaylistContributorRow[] = [];
  const seen = new Set<string>();

  for (const ref of [...playlist.refs].sort((a, b) => a.sequence - b.sequence)) {
    const contributor = ref.contributor ?? contributors.find((candidate) => resolveRoleId(candidate) === ref.roleId);

    if (!contributor) {
      if (import.meta.env.DEV) {
        console.warn('[contributor-playlist] unresolved ref', ref.roleId);
      }
      continue;
    }

    rows.push({
      roleId: ref.roleId,
      sequence: ref.sequence,
      contributor,
    });
    seen.add(contributor.id);
  }

  // Contributors with roles but missing playlist refs (e.g. created via role API before playlist sync)
  // still need to appear in the host/crew widget.
  let nextSequence = rows.length > 0 ? Math.max(...rows.map((row) => row.sequence)) + 1 : 1;
  for (const contributor of contributors) {
    if (seen.has(contributor.id)) continue;
    const roleId = resolveRoleId(contributor);
    if (!roleId) continue;
    rows.push({ roleId, sequence: nextSequence++, contributor });
  }

  return rows;
}

export function orderSessionCrewContributors(session: SessionDto, allContributors: ContributorDto[]): ContributorDto[] {
  const crewOnly = allContributors.filter((contributor) => {
    const role = getContributorSessionRoleGeneric(contributor, session);
    return role && (SESSION_ACTOR_ROLES as readonly string[]).includes(role.role);
  });

  return orderContributorsByPlaylist(crewOnly, getSessionCrewPlaylist(session), (contributor) => {
    const role = getContributorSessionRoleGeneric(contributor, session);
    return role?.id;
  });
}

export function orderFlowModerators(
  flow: FlowDto,
  allContributors: ContributorDto[],
  eventMultiFlow?: boolean,
): ContributorDto[] {
  const moderators = getFlowModerators(flow, allContributors, eventMultiFlow);
  return orderContributorsByPlaylist(moderators, getFlowModeratorsPlaylist(flow), (contributor) => {
    for (const role of contributor.roles) {
      if (role.role !== 'moderator') continue;
      if (role.entityType === 'Flow' || (eventMultiFlow === false && role.entityType === 'Event')) {
        return role.id;
      }
    }
    return undefined;
  });
}

export type SessionPlaylistSource = {
  contributorPlaylists?: SessionContributorPlaylistsDto;
};

export type FlowPlaylistSource = {
  contributorPlaylists?: FlowContributorPlaylistsDto;
};
